import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { DeliveryStatusEnum, IDeliveryNote, IDeliveryNotePosition, ShippingTypeEnum } from '../../models/delivery-note.model';
import { DeleteConfirmationData } from '../../models/modal-data.model';
import { IUser, UserRole } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { DeliveryNoteLockService, IDeliveryNoteLock } from '../../services/delivery-note-lock.service';
import { DeliveryNoteService } from '../../services/delivery-note.service';
import { ModalService } from '../../services/modal.service';
import { ToastService } from '../../services/toast.service';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { EmployeeIdModalComponent, EmployeeIdModalData } from '../employee-id-modal/employee-id-modal.component';

@Component({
  selector: 'app-delivery-note-overview-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './delivery-note-overview-modal.component.html',
  styleUrls: ['./delivery-note-overview-modal.component.scss'],
})
export class DeliveryNoteOverviewModalComponent implements OnInit, OnDestroy {
  @Input() deliveryNote: IDeliveryNote | null = null;
  @Input() orderInfo: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<IDeliveryNote>();
  @Output() noteUpdated = new EventEmitter<IDeliveryNote>();

  public isLocked = false;
  public lockInfo: IDeliveryNoteLock | null = null;
  public isDmsExported = false;
  public currentUser: IUser | null = null;
  public UserRole = UserRole;
  private currentEmployeeId = '';
  // Mitarbeiternummer confirmed via "WA Überprüfung" — accounts are shared, so the Geprüft checkboxes
  // stay disabled until someone confirms who is checking, and checks are recorded under this number.
  // Lives only as long as this overview is open.
  public waEmployeeId: string | null = null;
  private isEmployeeIdModalOpen = false;
  private subscriptions = new Subscription();
  public isExporting = false;

  constructor(
    public deliveryNoteService: DeliveryNoteService,
    private deliveryNoteLockService: DeliveryNoteLockService,
    private authService: AuthService,
    private toastService: ToastService,
    private modalService: ModalService
  ) { }

  ngOnInit(): void {
    this.isDmsExported = !!this.deliveryNote?.exportDate;

    if (this.deliveryNote?._id) {
      this.checkLockStatus();
    }

    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        if (user) {
          this.currentEmployeeId = String(user.employeeId || '');
          this.currentUser = user;
        }
      })
    );
  }

  private checkLockStatus(): void {
    if (!this.deliveryNote?._id) return;

    this.subscriptions.add(
      this.deliveryNoteLockService.getLock(this.deliveryNote._id).subscribe({
        next: response => {
          this.isLocked = !!response.DATA;
          this.lockInfo = response.DATA ?? null;
        },
        error: () => {
          this.isLocked = false;
          this.lockInfo = null;
        },
      })
    );
  }

  public getDeliveredCount(): number {
    if (!this.deliveryNote?.positions?.length) return 0;
    return this.deliveryNote.positions.filter(position => position.deliveryCheckout?.checked).length;
  }

  public getStatusName(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusName(status);
  }

  public getStatusClass(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusClass(status);
  }

  public getShippingTypeName(shippingType: string | undefined): string {
    const normalizedShippingType = String(shippingType || '');

    if (normalizedShippingType === ShippingTypeEnum.PALETTEDELIVERY || normalizedShippingType === 'Palettenversand') {
      return 'Palettenversand';
    }

    if (normalizedShippingType === ShippingTypeEnum.PARCELDELIVERY || normalizedShippingType === 'Paketversand') {
      return 'Paketversand';
    }

    return normalizedShippingType || '—';
  }

  public isPaletteShippingWarning(deliveryNote: IDeliveryNote): boolean {
    const shippingType = String(deliveryNote.shippingType || '');
    return (
      (shippingType === ShippingTypeEnum.PALETTEDELIVERY || shippingType === 'Palettenversand') &&
      deliveryNote.status !== DeliveryStatusEnum.DELIVERED
    );
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Summary calculations
  public get totalRollCount(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.rollCount || 0), 0) || 0;
  }

  public isDuplicateArticlePosition(index: number): boolean {
    const value = this.deliveryNote?.positions?.[index]?.articlePosition;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key || !this.deliveryNote?.positions) return false;

    let count = 0;
    for (const pos of this.deliveryNote.positions) {
      const k = pos?.articlePosition === null || pos?.articlePosition === undefined ? '' : String(pos.articlePosition).trim();
      if (k && k === key) count++;
      if (count > 1) return true;
    }
    return false;
  }

  public isLastOccurrenceArticlePosition(index: number): boolean {
    const value = this.deliveryNote?.positions?.[index]?.articlePosition;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key || !this.deliveryNote?.positions) return false;

    if (!this.isDuplicateArticlePosition(index)) return false;

    for (let i = index + 1; i < this.deliveryNote.positions.length; i++) {
      const k =
        this.deliveryNote.positions[i]?.articlePosition === null || this.deliveryNote.positions[i]?.articlePosition === undefined
          ? ''
          : String(this.deliveryNote.positions[i].articlePosition).trim();
      if (k && k === key) return false;
    }

    return true;
  }

  public getTotalPiecesForArticlePosition(index: number): number {
    const value = this.deliveryNote?.positions?.[index]?.articlePosition;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key || !this.deliveryNote?.positions) return 0;

    return this.deliveryNote.positions.reduce((sum, pos) => {
      const k = pos?.articlePosition === null || pos?.articlePosition === undefined ? '' : String(pos.articlePosition).trim();
      if (!k || k !== key) return sum;

      return sum + (pos.totalPieces || 0);
    }, 0);
  }

  public get totalFolie1(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.folie1 || 0), 0) || 0;
  }

  public get totalFolieN(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.folieN || 0), 0) || 0;
  }

  public get totalFolieA(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.folieA || 0), 0) || 0;
  }

  public get totalDryP(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.dryP || 0), 0) || 0;
  }

  public get totalUmCarton(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.umCarton || 0), 0) || 0;
  }

  public get totalAmmopack(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.ammopack || 0), 0) || 0;
  }

  public get totalCartonCount(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.cartonCount || 0), 0) || 0;
  }

  public get totalWeight(): number {
    return this.deliveryNote?.positions?.reduce((sum, pos) => sum + (pos.weightKg || 0), 0) || 0;
  }

  // A DMS-exported note is locked, so there's nothing to check off.
  public get canCheckPositions(): boolean {
    return !!this.waEmployeeId && !this.isDmsExported;
  }

  // "WA Überprüfung": same Mitarbeiternummer modal as on the create pages, prefilled with the number
  // confirmed last (or the account's own). It only resolves once the backend confirms the user exists.
  public openWaCheck(): void {
    if (this.isDmsExported) return;

    this.isEmployeeIdModalOpen = true;
    const data: EmployeeIdModalData = { employeeId: this.waEmployeeId ?? this.currentEmployeeId };
    this.modalService
      .open(EmployeeIdModalComponent, data)
      .then((employeeId: string | null) => {
        this.isEmployeeIdModalOpen = false;
        if (employeeId) this.waEmployeeId = employeeId;
      })
      .catch(() => {
        this.toastService.showError('Mitarbeiternummer konnte nicht bestätigt werden');
      });
  }

  public onCheckChange(position: IDeliveryNotePosition, event: Event): void {
    if (!this.deliveryNote?._id || !position._id) return;

    const checked = (event.target as HTMLInputElement).checked;
    if (!this.canCheckPositions || !this.waEmployeeId) {
      (event.target as HTMLInputElement).checked = !checked;
      return;
    }

    this.subscriptions.add(
      this.deliveryNoteService.checkPosition(this.deliveryNote._id, position._id, checked, this.waEmployeeId).subscribe({
        next: response => {
          if (response.DATA) {
            this.deliveryNote = response.DATA;
            this.noteUpdated.emit(response.DATA);
          }
        },
        error: () => {
          (event.target as HTMLInputElement).checked = !checked;
        },
      })
    );
  }

  public onClose(): void {
    this.close.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscape(event: Event): void {
    // Escape inside the Mitarbeiternummer modal only closes that modal, not this overview behind it.
    if (this.isEmployeeIdModalOpen) return;
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.onClose();
  }

  public onEdit(): void {
    if (this.deliveryNote) {
      this.edit.emit(this.deliveryNote);
    }
  }

  public exportPdf(): void {
    if (!this.deliveryNote?._id || this.isExporting) return;

    this.isExporting = true;
    this.subscriptions.add(
      this.deliveryNoteService.exportPdf(this.deliveryNote._id).subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const orderId = this.deliveryNote?.order?.internalOrderId ?? 'unbekannt';
          link.download = `Lieferzettel-${orderId}-${this.deliveryNote?.noteNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          this.isExporting = false;
        },
        error: () => {
          this.isExporting = false;
        },
      })
    );
  }

  public onQuickStatusChange(deliveryNote: IDeliveryNote, newStatus: DeliveryStatusEnum): void {
    if (!deliveryNote._id || deliveryNote.status === newStatus) return;

    if (newStatus === DeliveryStatusEnum.DELIVERED) {
      const allChecked = deliveryNote.positions?.every(p => p.deliveryCheckout?.checked === true);
      if (!allChecked) {
        this.toastService.showWarning('Bitte zuerst alle Positionen überprüfen');
        return;
      }
    }

    this.subscriptions.add(
      this.deliveryNoteService.updateDeliveryNoteStatus(deliveryNote._id, newStatus).subscribe({
        next: () => {
          // Update local state immediately for better UX
          deliveryNote.status = newStatus;
        },
        error: () => {
          console.error('Error updating delivery note status');
        },
      })
    );
  }

  public getNextStatus(currentStatus: DeliveryStatusEnum): DeliveryStatusEnum | null {
    return this.deliveryNoteService.getNextStatus(currentStatus);
  }

  public unlockDmsExport(): void {
    if (!this.deliveryNote?._id) return;

    this.modalService
      .open(DeleteConfirmationComponent, {
        title: 'Sperre aufheben',
        message: 'Sperre für diesen Lieferzettel aufheben?',
        confirmText: 'Aufheben',
        cancelText: 'Abbrechen',
      } as DeleteConfirmationData)
      .then((confirmed: boolean) => {
        if (!confirmed || !this.deliveryNote?._id) return;
        this.subscriptions.add(
          this.deliveryNoteService.unlockDeliveryNote(this.deliveryNote._id).subscribe({
            next: response => {
              if (response.DATA) {
                this.deliveryNote = response.DATA;
                this.isDmsExported = false;
                this.noteUpdated.emit(response.DATA);
                this.toastService.showSuccess('Sperre aufgehoben');
              }
            },
            error: () => {
              this.toastService.showError('Fehler beim Aufheben der Sperre');
            },
          })
        );
      })
      .catch(() => { });
  }

  ngOnDestroy(): void {
    if (this.isEmployeeIdModalOpen) this.modalService.close();
    this.subscriptions.unsubscribe();
  }
}
